import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { IconDefinitionConstants, ReportConstants } from "utils/constants";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import {
  ChartContextProvider,
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  ViewPanel,
  WidgetArea,
} from "components/common";
import * as IncomingInvoiceWidgets from "./widgets";
import useIncomingInvoiceReportFilterDefinition from "./forms/useReportFilterDefinition";
import IncomingInvoiceListView from "./listview";
import IncomingInvoiceReportTitle from "./reportTitle";
import { Grid } from "@mui/material";
import { useIncomingInvoiceService } from "serviceHooks";
import useIncomingInvoicePermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const IncomingInvoiceReport: React.FC = () => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const {
    filterFormFields: reportFilterDefinition,
    filterValueFormatter,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
  } = useIncomingInvoiceReportFilterDefinition();

  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.IncomingInvoices);

  const tableRef = React.useRef<TableHandles>(null);

  const fileName = `${title} - ${companyName}`;

  const exportReportExcel = async () => {
    tableRef.current?.exportToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportToCSV();
  };

  const { canViewIncomingInvoices, canViewCharts } = useIncomingInvoicePermissions();

  return (
    <ReloadEvent
      uniqueKey={ReportConstants.ReportKeys.IncomingInvoice}
      render={(reloadKey) => (
        <PageLayouts.DashboardLayout<IncomingInvoiceFormTypes.Filter>
          entityKey={0}
          title={<IncomingInvoiceReportTitle />}
          topRightButtons={
            <DashboardButtonGroup
              buttons={[<FilterButton />]}
              moreActions={[
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportExcel,
                },
                {
                  description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                  icon: IconDefinitionConstants.WSave,
                  onClickHandler: exportReportCSV,
                },
              ]}
            />
          }
          filterFields={reportFilterDefinition}
          initialFilter={{}}
          hasPermissions={canViewIncomingInvoices}
          filterValueFormatter={filterValueFormatter}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.IncomingInvoice}
          reportToAPIFilterFormatter={incomingInvoiceService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
        >
          {canViewCharts ? (
            <WidgetArea>
              <ChartContextProvider>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.IncomingInvoice.StatusGraphTitle
                  )}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                >
                  <IncomingInvoiceWidgets.TotalByStatusChart />
                </ViewPanel>
                <ViewPanel
                  title={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.IncomingInvoice.DueInGraphTitle
                  )}
                  appearance="sm"
                  gridSize="md"
                  isCanvasContent
                >
                  <IncomingInvoiceWidgets.TotalByDueDateChart />
                </ViewPanel>
              </ChartContextProvider>
            </WidgetArea>
          ) : (
            <></>
          )}
          <Grid container item>
            <IncomingInvoiceListView reloadKey={reloadKey} ref={tableRef} exportFileName={fileName} />
          </Grid>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default IncomingInvoiceReport;
